import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingCTA from '../../components/feature/FloatingCTA';

export default function ContactPage() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) return;
    setStatus('sending');
    try {
      const formData = new FormData(form);
      const body = new URLSearchParams(Array.from(formData.entries()).map(([k, v]) => [k, String(v)]));
      const res = await fetch('https://readdy.ai/api/form/d6tdk40q778icnc8un6g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) { setStatus('success'); form.reset(); }
      else setStatus('error');
    } catch { setStatus('error'); }
  };

  return (
    <>
      <Header />
      <FloatingCTA />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-brand-dark">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 text-brand-amber text-xs font-bold uppercase tracking-widest mb-5">
            <div className="h-px w-8 bg-brand-amber" />
            Contact Us
            <div className="h-px w-8 bg-brand-amber" />
          </div>
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-white mb-5">
            Book Your Service or Get a Free Quote
          </h1>
          <p className="text-white/70 text-xl">Jacksonville's chimney, air duct, and dryer vent cleaning specialists. We respond within 1 hour.</p>
        </div>
      </section>

      {/* Contact section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Left: Info */}
            <div className="lg:w-2/5">
              <h2 className="font-serif text-3xl font-bold text-brand-dark mb-6">Get In Touch</h2>
              <div className="space-y-5 mb-8">
                {[
                  { icon: 'ri-phone-fill', label: 'Call or Text', value: '(904) 555-0191', href: 'tel:+19045550191' },
                  { icon: 'ri-mail-fill', label: 'Email Us', value: 'info@bbmservices.com', href: 'mailto:info@bbmservices.com' },
                  { icon: 'ri-map-pin-fill', label: 'Location', value: 'Jacksonville, Florida 32099', href: null },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl border border-stone-200">
                    <div className="w-11 h-11 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
                      <i className={`${item.icon} text-brand-amber text-lg`} />
                    </div>
                    <div>
                      <div className="text-brand-warm-gray text-xs uppercase tracking-wide font-semibold">{item.label}</div>
                      {item.href ? (
                        <a href={item.href} className="text-brand-dark font-semibold hover:text-brand-amber transition-colors cursor-pointer">{item.value}</a>
                      ) : (
                        <div className="text-brand-dark font-semibold">{item.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-stone-50 rounded-xl border border-stone-200 p-5 mb-6">
                <h3 className="font-semibold text-brand-dark mb-3">Business Hours</h3>
                <div className="space-y-2">
                  {[
                    { days: 'Monday – Friday', hours: '8:00am – 6:00pm' },
                    { days: 'Saturday', hours: '9:00am – 3:00pm' },
                    { days: 'Sunday', hours: 'Closed' },
                  ].map((h) => (
                    <div key={h.days} className="flex justify-between text-sm">
                      <span className="text-brand-warm-gray">{h.days}</span>
                      <span className="text-brand-dark font-medium">{h.hours}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl overflow-hidden h-52 border border-stone-200">
                <iframe
                  title="BBM Services Jacksonville FL"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d110743!2d-81.6557!3d30.3322!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e5b716f1ceafeb%3A0xc4cd7d3896fcc7e3!2sJacksonville%2C%20FL!5e0!3m2!1sen!2sus!4v1"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:w-3/5">
              <div className="bg-white rounded-2xl border border-stone-200 p-8 lg:p-10">
                {status === 'success' ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                      <i className="ri-check-double-fill text-green-600 text-3xl" />
                    </div>
                    <h3 className="font-serif text-2xl font-bold text-brand-dark mb-2">Message Sent!</h3>
                    <p className="text-brand-warm-gray">We'll get back to you within the hour. Thank you for contacting BBM Services!</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form>
                    <h3 className="font-serif text-2xl font-bold text-brand-dark mb-6">Send Us a Message</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wide mb-1.5">First Name *</label>
                        <input type="text" name="first_name" required className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-brand-amber transition-colors" placeholder="John" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wide mb-1.5">Last Name *</label>
                        <input type="text" name="last_name" required className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-brand-amber transition-colors" placeholder="Smith" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wide mb-1.5">Phone *</label>
                        <input type="tel" name="phone" required className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-brand-amber transition-colors" placeholder="(904) 555-0000" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wide mb-1.5">Email</label>
                        <input type="email" name="email" className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-brand-amber transition-colors" placeholder="john@email.com" />
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wide mb-1.5">Service Needed</label>
                      <select name="service" className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-brand-amber transition-colors">
                        <option value="">Select a service...</option>
                        <option value="Chimney Cleaning">Chimney Cleaning</option>
                        <option value="Air Duct Cleaning">Air Duct Cleaning</option>
                        <option value="Dryer Vent Cleaning">Dryer Vent Cleaning</option>
                        <option value="Multiple Services">Multiple Services</option>
                        <option value="Not Sure">Not Sure – Need Advice</option>
                      </select>
                    </div>
                    <div className="mb-4">
                      <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wide mb-1.5">Your Address</label>
                      <input type="text" name="address" className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-brand-amber transition-colors" placeholder="Jacksonville, FL zip code" />
                    </div>
                    <div className="mb-4">
                      <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wide mb-1.5">Preferred Date</label>
                      <input type="date" name="preferred_date" className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-brand-amber transition-colors" />
                    </div>
                    <div className="mb-6">
                      <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wide mb-1.5">Message</label>
                      <textarea name="message" rows={4} maxLength={500} className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-brand-amber transition-colors resize-none" placeholder="Tell us more about what you need... (max 500 characters)" />
                    </div>
                    <button type="submit" disabled={status === 'sending'} className="w-full bg-brand-amber hover:bg-brand-amber-dark text-white font-bold py-4 rounded-lg text-base transition-all cursor-pointer whitespace-nowrap disabled:opacity-60">
                      {status === 'sending' ? 'Sending...' : 'Send Message'}
                    </button>
                    {status === 'error' && <p className="text-red-500 text-sm text-center mt-3">Something went wrong. Please call us directly at (904) 555-0191.</p>}
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
